import { HttpContextToken } from '@angular/common/http';

// the lead time in milliseconds before token expiration before the token is refreshed
export const TOKEN_REFRESH_LEAD_TIME_IN_MS = 1000 * 60; // 1 minute

export type AuthToken = {
  token: string;
  expiresAt?: Date;
};

export type NamedAuthToken = AuthToken & { name: string };

export type B2CTokenRequestDto = { b2cTokenRequest: boolean };

export type B2CAccessTokenRequestDto = { b2cAccessTokenRequest: boolean; scope: string[] };

export const B2C_SERVICE_TOKEN_REQUEST_DTO: B2CTokenRequestDto = { b2cTokenRequest: true };

export type ServiceTokenRequestDto = {
  clientId: string;
  orgId: string;
};

export type TokenRequestDto = ServiceTokenRequestDto | B2CTokenRequestDto | B2CAccessTokenRequestDto;

export function isB2CTokenRequestDto(dto: TokenRequestDto): dto is B2CTokenRequestDto {
  return (dto as B2CTokenRequestDto)?.b2cTokenRequest === true;
}

export function isB2CAccessTokenRequestDto(dto: TokenRequestDto): dto is B2CAccessTokenRequestDto {
  return (dto as B2CAccessTokenRequestDto)?.b2cAccessTokenRequest === true;
}

export function isB2CRequest(dto: TokenRequestDto): boolean {
  return isB2CTokenRequestDto(dto) || isB2CAccessTokenRequestDto(dto);
}

export function isServiceAccessTokenRequestDto(dto: TokenRequestDto): dto is ServiceTokenRequestDto {
  return (dto as ServiceTokenRequestDto)?.clientId !== undefined;
}

/**
 * The AUTH_CONTEXT is a HttpContextToken which can be used to pass additional auth context information to interceptors.
 */
export const AUTH_CONTEXT = new HttpContextToken<AuthContextsForEndPoints>(() => ({}));

/**
 * The AuthContext is a map of key-value pairs which can be used to pass additional information to the token provider.
 *
 * E.g. for Portals we create the following auth context:
 * { portalId: <uuid> }
 *
 * Our token endpoint gets this context and creates a token for this specific portal, which
 * can later be used to access portal specific resources.
 */
export type AuthContext = { [name: string]: string };

/**
 * Map token endpoints to AuthContexts.
 *
 * This is used to pass different auth contexts for different endpoints and allows for adding multiple tokens for a given request.
 *
 * E.g. for guest logins on Portals we create the following auth context:
 * {
 * '<portal token endpoint>': { portalId: <uuid> },
 * '<guest token endpoint>': { code: <code> }
 * }
 *
 */
export type AuthContextsForEndPoints = { [endpoint: string]: AuthContext };

/**
 * Represents the authentication context associated with a specific endpoint.
 */
export type AuthContextForEndPoint = {
  endpoint: string;
  context?: AuthContext;
};

/**
 * Common options for getting an authentication token.
 */
export type AuthTokenOptions = {
  tokenLeadTimeInMilliseconds?: number;
  noRefetch?: boolean; // if set, a token will be returned from the authentication framework if it's available in cache and not expired, but not refetched
};

/**
 * The request options used for fetching auth tokens from multiple endpoints.
 */
export type RequestOptionsForMultipleTokens = AuthTokenOptions & {
  endpointsWithContexts?: AuthContextsForEndPoints;
};

/**
 * The request options used for fetching an auth token from a single endpoint.
 */
export type RequestOptionsForSingleToken = AuthTokenOptions & AuthContextForEndPoint;
